import React from 'react';
import { Col, FormGroup } from 'reactstrap';
import { useFieldArray } from 'react-hook-form';
import { InputField, RHFField } from '../../components/Field';

export const integrationInitialValues = {
  enabled: true,
  integration: '',
  interface: '',
  connection: {
    type: 'HTTP',
    uri: '',
    clientCertificate: '',
    trustCertificate: '',
    authType: 'NONE',
    username: '',
    password: '',
    accessOperation: '',
    accessToken: '',
    refreshOperation: '',
    refreshToken: '',
  },
};

export const integrationOperationInitialValues = {
  enabled: true,
  operation: '',
  interfaceType: 'HTTP',
  settings: [],
  request: {
    inputContentType: '*/*',
    outputContentType: '*/*',
    method: 'GET',
    path: '',
    transforms: [],
    parameters: [],
    headers: [],
  },
  response: {
    inputContentType: '*/*',
    outputContentType: '*/*',
    method: 'GET',
    path: '',
    transforms: [],
    parameters: [],
    headers: [],
  },
};

export const inputContentTypeList = [
  '*/*',
  'text/plain',
  'text/xml',
  'application/json',
  'application/x-www-form-urlencoded',
];

// ------------------------------------------------------------

export const arrToObjForFieldArr = (arr = []) => {
  if (!Array.isArray(arr)) return {};
  const obj = {};
  arr.forEach((item) => {
    obj[item.name] = item.value;
  });
  return obj;
};

export const objToArrForFieldArr = (obj = {}) => {
  const arr = [];
  Object.keys(obj).forEach((name) => {
    arr.push({ name, value: obj[name] });
  });
  return arr;
};

// FieldArray renderers, migrated from redux-form's `{ fields, meta }` render-prop
// shape to self-contained react-hook-form components. Each owns its own
// useFieldArray(control, name); `register` is threaded from the parent form so the
// plain inputs/selects/textarea bind into the same form.

export const RenderSettings = ({ control, register, name }) => {
  const { fields, append, remove } = useFieldArray({ control, name });
  return (
    <>
      {fields.map((field, index) => (
        <div style={{ width: '100%', position: 'relative' }} key={field.id}>
          <div style={{ display: 'table', width: '100%', height: 40 }}>
            <div style={{ display: 'table-cell', width: 150, paddingRight: 5 }}>
              <input
                {...register(`${name}.${index}.name`)}
                placeholder="Name"
                type="text"
                className="form-control"
                required={true}
              />
            </div>
            <div style={{ display: 'table-cell' }}>
              <input
                {...register(`${name}.${index}.value`)}
                placeholder="Value"
                type="text"
                className="form-control"
                required={true}
              />
            </div>
          </div>
          <div style={{ position: 'absolute', top: 8, right: -20 }}>
            <i
              className="icon-minus"
              onClick={() => remove(index)}
              style={{ cursor: 'pointer' }}
              title="Remove Setting"
            />
          </div>
        </div>
      ))}
      <FormGroup row>
        <Col md="12">
          <div className="float-end" style={{ paddingLeft: 5 }}>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => append({})}>
              Add Setting
            </button>
          </div>
        </Col>
      </FormGroup>
    </>
  );
};

export const RenderParameters = ({ control, register, name, title }) => {
  const { fields, append, remove } = useFieldArray({ control, name });
  return (
    <FormGroup row>
      <ColLeft>Send {title}</ColLeft>
      <ColRight>
        {fields.map((field, index) => (
          <div style={{ width: '100%', position: 'relative' }} key={field.id}>
            <div style={{ display: 'table', width: '100%', height: 40 }}>
              <div style={{ display: 'table-cell', width: 150 }}>
                <input
                  {...register(`${name}.${index}.name`)}
                  placeholder="Name"
                  type="text"
                  className="form-control"
                  required={true}
                />
              </div>
              <div style={{ display: 'table-cell', width: 20, textAlign: 'center' }}>=</div>
              <div style={{ display: 'table-cell' }}>
                <input
                  {...register(`${name}.${index}.value`)}
                  placeholder="Value"
                  type="text"
                  className="form-control"
                  required={true}
                />
              </div>
            </div>
            <div style={{ position: 'absolute', top: 8, right: -20 }}>
              <i className="icon-minus" onClick={() => remove(index)} style={{ cursor: 'pointer' }} title="Remove Item" />
            </div>
          </div>
        ))}
        <div className="float-end">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => append({})}>
            Add {title}
          </button>
        </div>
      </ColRight>
    </FormGroup>
  );
};

export const RenderTransforms = ({ control, register, name }) => {
  const { fields, append, remove } = useFieldArray({ control, name });
  return (
    <FormGroup row>
      <ColLeft>Transform</ColLeft>
      <ColRight>
        {fields.map((field, index) => (
          <div key={field.id} style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', top: 8, right: -20 }}>
              <i
                className="icon-minus"
                onClick={() => remove(index)}
                style={{ cursor: 'pointer' }}
                title="Remove Transform"
              />
            </div>
            <table style={{ width: '100%' }}>
              <tbody>
                <tr style={{ height: 40 }}>
                  <td valign="top" width={100}>
                    <div style={{ paddingTop: 8 }}>Description</div>
                  </td>
                  <td>
                    <RHFField
                      name={`${name}.${index}.description`}
                      control={control}
                      component={InputField}
                      id={`${name}.${index}.description`}
                    />
                  </td>
                </tr>
                <tr style={{ height: 40 }}>
                  <td valign="top">
                    <div style={{ paddingTop: 8 }}>Type</div>
                  </td>
                  <td>
                    <select {...register(`${name}.${index}.type`)} className="form-control">
                      {['RAW', 'AUTO', 'JAVASCRIPT', 'XSLT', 'JOLT', 'THYMELEAF', 'HANDLEBARS'].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
                <tr style={{ height: 40 }}>
                  <td valign="top">
                    <div style={{ paddingTop: 8 }}>Input</div>
                  </td>
                  <td>
                    <select {...register(`${name}.${index}.inputContentType`)} required className="form-control">
                      {inputContentTypeList.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
                <tr style={{ height: 40 }}>
                  <td valign="top">
                    <div style={{ paddingTop: 8 }}>Transform</div>
                  </td>
                  <td>
                    <textarea
                      {...register(`${name}.${index}.value`)}
                      className="form-control"
                      style={{ minHeight: 150 }}
                    />
                  </td>
                </tr>
                <tr style={{ height: 40 }}>
                  <td valign="top">
                    <div style={{ paddingTop: 8 }}>Output</div>
                  </td>
                  <td>
                    <select {...register(`${name}.${index}.outputContentType`)} required className="form-control">
                      {inputContentTypeList.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
            <div style={{ marginTop: 10 }}>
              <hr size={1} />
            </div>
          </div>
        ))}
        <div className="float-end">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() =>
              append({
                description: '',
                type: 'RAW',
                inputContentType: '*/*',
                value: '',
                outputContentType: '*/*',
              })
            }
          >
            Add Transform
          </button>
        </div>
      </ColRight>
    </FormGroup>
  );
};

export const ColLeft = ({ children, ...props }) => (
  <Col sm={3} lg={2} xl={2} {...props}>
    {children}
  </Col>
);

export const ColRight = ({ children, ...props }) => (
  <Col sm={9} lg={10} xl={10} {...props}>
    {children}
  </Col>
);
